import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Select,
  Paper,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  IconButton,
  InputLabel,
  FormControl,
  ListItemText,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import { toast } from 'react-toastify';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import * as Yup from 'yup';

import { updateMe } from '../services/users';
import { getLanguages } from '../services';
import SubviewContainer from '../components/SubviewContainer';
import OverLayLoader from '../components/OverLayLoader';
import useTitle from '../hooks/useTitle';
import { useAuth } from '../context/auth/AuthContext';
import { CONSTANT_KEYS } from '../helpers/tableKeys';
import { createErrorNotification } from '../utils/createErrorNotification';
import { clearProfilesFromSessionStorage } from '../utils/clearProfilesFromSessionStorage';
import PATH from '../helpers/routes.json';

const { PROFILES, USERNAME, DATA_LANGUAGE_ID } = CONSTANT_KEYS;

const UserSettingsView = () => {
  const [userData, setUserData] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { t } = useTranslation(['promptbar', 'users']);

  const { user, setUser } = useAuth();

  const title = t('promptbar:profileSettings');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  useEffect(() => {
    setUserData(user);
  }, [user]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setIsOptionsLoading(true);
        const [langs] = await Promise.all([getLanguages()]);

        setLanguages(langs.data);
      } catch (error) {
        console.log('error', error);
      } finally {
        setIsOptionsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  const passwordSchema = Yup.object({
    password: Yup.string().test(
      'password-min',
      t('passwordMin'),
      value => !value || value.length >= 8,
    ),

    confirmPassword: Yup.string().when('password', {
      is: value => Boolean(value),
      then: schema =>
        schema
          .required(t('confirmPasswordRequired'))
          .oneOf([Yup.ref('password')], t('passwordsMismatch')),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const selectedProfiles =
    userData?.[PROFILES]?.filter(p => p.is_default).map(p => p.profile_name) ??
    [];

  const handleSelectChange = event => {
    const { value, name } = event.target;

    switch (name) {
      case PROFILES:
        setUserData(prev => ({
          ...prev,
          [PROFILES]: prev[PROFILES].map(profile => ({
            ...profile,
            is_default: value.includes(profile.profile_name),
          })),
        }));
        break;

      default:
        setUserData(prev => ({ ...prev, [name]: value }));
        break;
    }
  };

  const handlePasswordChange = async evt => {
    const { name, value } = evt.target;
    const newValues = { ...password, [name]: value };

    setPassword(newValues);

    const isEmpty = !newValues.password && !newValues.confirmPassword;

    if (isEmpty) {
      setErrors({});
      return;
    }

    try {
      await passwordSchema.validateAt(name, newValues);
      setErrors(prev => ({ ...prev, [name]: '' }));
    } catch (err) {
      setErrors(prev => ({ ...prev, [name]: err.message }));
    }
  };

  const handleShowPassword = () => setShowPassword(prev => !prev);
  const handleShowConfirmPassword = () => setShowConfirmPassword(prev => !prev);

  const cancelHandler = () => {
    navigate(-1);
  };

  const handleFormSubmit = async evt => {
    try {
      setIsLoading(true);
      const newData = {
        ...(hasPassword
          ? {
              password: password.password,
            }
          : {}),

        data_language_id: userData[DATA_LANGUAGE_ID],
        profiles: userData[PROFILES],
      };

      const res = await updateMe(newData);
      if (res?.data) {
        clearProfilesFromSessionStorage();
        setUser(res.data);
      }
      toast.success(t('changesSaved'));
    } catch (error) {
      createErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  // пароли
  const hasPassword = Boolean(password.password || password.confirmPassword);

  const isPasswordValid =
    !hasPassword ||
    (password.password &&
      password.confirmPassword &&
      !errors.newPassword &&
      !errors.confirmPassword);

  const isFormValid =
    Object.values(errors).every(err => !err) && isPasswordValid;

  if (!userData || isOptionsLoading) {
    return <OverLayLoader />;
  }

  const formRender = (
    <Box component="form" autoComplete="off">
      <FormControl fullWidth variant="standard">
        <InputLabel htmlFor="TextField-1" shrink={false}>
          {t('users:table.username')}
        </InputLabel>
        <TextField
          id="TextField-1"
          disabled
          name={USERNAME}
          value={userData[USERNAME]}
        />
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel id="TextField-2" shrink={false}>
          {t('users:defaultDictionaryLanguage')}
        </InputLabel>
        <Select
          labelId="TextField-2"
          id="Select-1"
          name={DATA_LANGUAGE_ID}
          value={userData[DATA_LANGUAGE_ID]}
          input={<OutlinedInput label={t('users:defaultDictionaryLanguage')} />}
          IconComponent={KeyboardArrowDownIcon}
          onChange={handleSelectChange}
        >
          {languages.map(lng => (
            <MenuItem key={lng.id} value={lng.id}>
              <ListItemText primary={lng.name.toUpperCase()} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel id="TextField-3" shrink={false}>
          {t('users:defaultProfile')}
        </InputLabel>
        <Select
          labelId="TextField-3"
          id="Select-2"
          name={PROFILES}
          multiple
          value={selectedProfiles}
          label={t('users:defaultProfile')}
          input={<OutlinedInput label={t('users:defaultProfile')} />}
          IconComponent={KeyboardArrowDownIcon}
          onChange={handleSelectChange}
          renderValue={selected => selected.join(', ')}
        >
          {userData[PROFILES].map(el => (
            <MenuItem key={el.profile_name} value={el.profile_name}>
              <Checkbox checked={selectedProfiles.includes(el.profile_name)} />
              <ListItemText primary={el.profile_name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel htmlFor="TextField-4" shrink={false}>
          {t('changePassword')}
        </InputLabel>
        <TextField
          id="TextField-4"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={password.password}
          onChange={handlePasswordChange}
          error={Boolean(errors.password)}
          helperText={errors.password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleShowPassword}
                  edge="end"
                  size="small"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
        <InputLabel htmlFor="TextField-3" shrink={false}>
          {t('confirmPassword')}
        </InputLabel>
        <TextField
          id="TextField-3"
          autoComplete="new-password"
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={password.confirmPassword}
          onChange={handlePasswordChange}
          error={Boolean(errors.confirmPassword)}
          helperText={errors.confirmPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleShowConfirmPassword}
                  edge="end"
                  size="small"
                  aria-label="toggle confirm password visibility"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <Box sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}>
        <Button
          type="button"
          color="secondary"
          sx={{ mr: 1 }}
          variant="contained"
          onClick={cancelHandler}
        >
          {t('promptbar:cancel')}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={!isFormValid}
          onClick={handleFormSubmit}
        >
          {t('promptbar:save')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {isLoading && <OverLayLoader />}

      {pathname === PATH.user.primary ? (
        <SubviewContainer title={title}>
          <Box sx={{ padding: '3rem 2rem 2rem' }}>{formRender}</Box>
        </SubviewContainer>
      ) : (
        <Box px={15}>
          <Paper elevation={3} sx={{ padding: '2.5rem 2rem 2rem' }}>
            {formRender}
          </Paper>
        </Box>
      )}
    </>
  );
};

export default UserSettingsView;
